"use client";

/**
 * 设备识别工具 — device_id + 浏览器指纹
 * - device_id: 首次生成 UUID 存 localStorage，标识"这台浏览器"
 * - fingerprint: 采集强特征（canvas/屏幕/时区/语言/平台），用于跨浏览器识别同一台电脑
 */

const DEVICE_ID_KEY = "rwai-device-id";

/** 生成 UUID v4 */
export function generateDeviceId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** 获取（或生成）设备 ID — 存 localStorage，同一浏览器持久 */
export function getDeviceId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = generateDeviceId();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return generateDeviceId();
  }
}

/** canvas 指纹（同机同浏览器稳定，跨浏览器弱） */
function getCanvasFingerprint(): string | null {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 60;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillStyle = "#f60";
    ctx.fillRect(10, 10, 100, 30);
    ctx.fillStyle = "#069";
    ctx.fillText("RWAI-Device-Fingerprint-2026", 4, 16);
    return canvas.toDataURL().slice(0, 200);
  } catch {
    return null;
  }
}

/** 采集浏览器指纹（强特征为主） */
export function getFingerprint(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  const dpr = window.devicePixelRatio || 1;
  return {
    canvas: getCanvasFingerprint(),
    screen: `${window.screen.width}x${window.screen.height}x${dpr}`,
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      String(new Date().getTimezoneOffset()),
    language: navigator.language || "",
    platform: navigator.platform || "",
    ua: navigator.userAgent || "",
  };
}

/** 设备备注（默认取浏览器 + 系统） */
export function getDeviceName(): string {
  if (typeof window === "undefined") return "";
  const ua = navigator.userAgent || "";
  let browser = "浏览器";
  if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("Chrome/")) browser = "Chrome";
  else if (ua.includes("Firefox/")) browser = "Firefox";
  else if (ua.includes("Safari/")) browser = "Safari";
  const os = navigator.platform || "";
  return `${browser} (${os})`;
}
